import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { type ProductOperatorActionFormValues, productOperatorActionFormSchema } from "../../schemas"
import type { ProductQualityInstructions } from "../../types"

import { ProductOperatorActionForm } from "./ProductOperatorActionForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"

interface ProductOperatorActionFormDialogProps {
  isOpen: boolean
  onClose: () => void
  instructions?: ProductQualityInstructions
  onSubmit: (input: ProductQualityInstructions) => Promise<void>
  isUpdating: boolean
}

export const ProductOperatorActionFormDialog: FC<ProductOperatorActionFormDialogProps> = ({
  isOpen,
  onClose,
  instructions,
  onSubmit,
  isUpdating,
}) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const methods = useForm<ProductOperatorActionFormValues>({
    defaultValues: {
      isOperatorActionRequired: instructions?.isOperatorActionRequired ?? false,
      operatorActionDescription: instructions?.operatorActionDescription ?? "",
    },
    resolver: zodResolver(productOperatorActionFormSchema),
    mode: "onChange",
  })

  const {
    formState: { isDirty, isValid, isSubmitting },
    handleSubmit,
    reset,
  } = methods

  const canSubmit = isDirty && isValid && !isSubmitting

  const handleClose = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
      reset()
    }
  }

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const handleConfirmClose = () => {
    setIsCancelDialogOpen(false)
    onClose()
    reset()
  }

  const submitForm = async (formValues: ProductOperatorActionFormValues) => {
    await onSubmit(formValues)
    reset(formValues)
  }

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleConfirmClose} />
  ) : (
    <Dialog open={isOpen} fullWidth onClose={handleClose} data-testid='operator-action-form-dialog'>
      <DialogHeader title='Edit Operator Action' onClose={handleClose} />
      <DialogContent className='p-10'>
        <FormProvider {...methods}>
          <ProductOperatorActionForm />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button data-testid='cancel-button' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button
          data-testid='submit-button'
          variant='primary'
          disabled={!canSubmit || isUpdating}
          onClick={handleSubmit(submitForm)}
        >
          {isUpdating ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
